import { useMutation, useQuery } from '@apollo/client';
import { defTitle } from 'assets/strings';
import {
    GRAPHQL_CATALOGO_ACOES,
    GRAPHQL_PERFIL_ACOES,
    GRAPHQL_SALVAR_ACOES_PERFIL,
} from 'gql/perfil';
import { usePermissao } from 'hooks/usePermissao';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from 'routes/auth.context';
import CardResource, { IResourceCatalogo } from './components/CardResource';
import { useSelecaoAcoes } from './hooks/useSelecaoAcoes';
import { Container, Grade, Secao, Topo, Vazio, Voltar } from './styl';

const ACOES_EDITAVEIS = [
    'visualizar',
    // lista-coleta
    'agrupar',
    'desagrupar',
    'status_manual',
    'voltar_status_salvo',
    'editar_cliente',
    'editar_pagamento',
    'cancelar',
];

interface IGrupo {
    secao: IResourceCatalogo | null;
    itens: IResourceCatalogo[];
}

const chaveVisualizar = (r: IResourceCatalogo) =>
    r.acoes.find(a => a.id === 'visualizar')?.chave;

const semAcentos =(t: string) =>
    (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const PermissoesPerfil: FC = () => {
    const { perfilId } = useParams<{ perfilId: string }>();
    const { alertConfirm } = useContext(AuthContext);
    const { can } = usePermissao();
    const [busca, setBusca] = useState('');

    const { data: dataCatalogo, loading: loadingCatalogo } = useQuery(
        GRAPHQL_CATALOGO_ACOES,
        { fetchPolicy: 'no-cache' },
    );
    // `perfil(id)` no backend ignora o id; lista todos e escolhe pelo da rota.
    const {
        data: dataPerfil,
        loading: loadingPerfil,
        refetch,
    } = useQuery(GRAPHQL_PERFIL_ACOES, { fetchPolicy: 'no-cache' });
    const [salvar, { loading: salvando }] = useMutation(
        GRAPHQL_SALVAR_ACOES_PERFIL,
    );

    // Só as ações de ACOES_EDITAVEIS aparecem na tela. As demais ações do perfil
    // continuam em `selecionadas` e são regravadas sem alteração ao salvar.
    const catalogo: IResourceCatalogo[] = useMemo(
        () =>
            (dataCatalogo?.catalogoAcoes || [])
                .map((r: IResourceCatalogo) => ({
                    ...r,
                    acoes: r.acoes.filter(a => ACOES_EDITAVEIS.includes(a.id)),
                }))
                .filter((r: IResourceCatalogo) => r.acoes.length > 0),
        [dataCatalogo],
    );
    const perfil = (dataPerfil?.perfil || []).find(
        p => String(p.id) === String(perfilId),
    );
    const iniciais: string[] = useMemo(() => perfil?.acoes || [], [perfil]);
    const { selecionadas, alternar, marcarTodos, alterado } =
        useSelecaoAcoes(iniciais);

    useEffect(() => {
        document.title = `${defTitle} - Permissões`;
    }, []);

    // O catálogo vem na ordem do menu: cada título de seção (sem link) agrupa as
    // telas que vêm depois dele, até o próximo título.
    const grupos: IGrupo[] = useMemo(() => {
        const lista: IGrupo[] = [];
        catalogo.forEach(r => {
            if (!r.link) {
                lista.push({ secao: r, itens: [] });
                return;
            }
            if (lista.length === 0) lista.push({ secao: null, itens: [] });
            lista[lista.length - 1].itens.push(r);
        });
        return lista.filter(g => g.itens.length > 0);
    }, [catalogo]);

    const telas = useMemo(() => grupos.flatMap(g => g.itens), [grupos]);
    const marcadas = telas.filter(r =>
        selecionadas.has(chaveVisualizar(r) || ''),
    ).length;

    const gruposVisiveis: IGrupo[] = useMemo(() => {
        const termo = semAcentos(busca.trim());
        if (!termo) return grupos;
        return grupos
            .map(g =>
                g.secao && semAcentos(g.secao.titulo).includes(termo)
                    ? g
                    : {
                          ...g,
                          itens: g.itens.filter(r =>
                              semAcentos(r.titulo).includes(termo),
                          ),
                      },
            )
            .filter(g => g.itens.length > 0);
    }, [grupos, busca]);

    // O título da seção só aparece no menu com o próprio "visualizar": marca se
    // alguma tela da seção estiver visível e desmarca se nenhuma estiver.
    const acoesParaSalvar = () => {
        const final = new Set(selecionadas);
        grupos.forEach(({ secao, itens }) => {
            const chave = secao && chaveVisualizar(secao);
            if (!chave) return;
            const algumaVisivel = itens.some(r =>
                final.has(chaveVisualizar(r) || ''),
            );
            if (algumaVisivel) final.add(chave);
            else final.delete(chave);
        });
        return Array.from(final);
    };

    const handleSalvar = async () => {
        try {
            await salvar({
                variables: {
                    perfilId: Number(perfilId),
                    acoes: acoesParaSalvar(),
                },
            });
            await alertConfirm({
                tipo: 'info',
                titulo: 'Permissões salvas',
                conteudo: 'As permissões do perfil foram atualizadas.',
            });
            await refetch();
        } catch ({ message }: any) {
            await alertConfirm({
                tipo: 'erro',
                titulo: 'Erro',
                conteudo: message as string,
            });
        }
    };

    if (loadingCatalogo || loadingPerfil) {
        return <Vazio>Carregando...</Vazio>;
    }
    if (!perfil) {
        return <Vazio>Perfil não localizado</Vazio>;
    }

    return (
        <Container>
            <Topo>
                <div className="titulo">
                    <Voltar to="/admin/perfil">
                        <span className="icon-voltar" aria-hidden="true" />
                        Perfis de acesso
                    </Voltar>
                    <h1>Permissões de {perfil.nome}</h1>
                    <p>
                        {marcadas} de {telas.length} telas liberadas para
                        visualizar
                        {alterado && (
                            <span className="pendente">
                                {' '}
                                · alterações não salvas
                            </span>
                        )}
                    </p>
                </div>
                <input
                    type="search"
                    placeholder="Buscar tela"
                    aria-label="Buscar tela"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                />
                {can('perfil.editar') && (
                    <button
                        type="button"
                        className="salvar"
                        disabled={!alterado || salvando}
                        onClick={handleSalvar}
                    >
                        {salvando ? 'Salvando...' : 'Salvar'}
                    </button>
                )}
            </Topo>
            {gruposVisiveis.length === 0 && (
                <Vazio>Nenhuma tela encontrada para "{busca}".</Vazio>
            )}
            {gruposVisiveis.map(({ secao, itens }) => (
                <Secao key={secao?.id || 'sem-secao'}>
                    {secao && (
                        <h2>
                            {secao.titulo}
                            <span>
                                {
                                    itens.filter(r =>
                                        selecionadas.has(
                                            chaveVisualizar(r) || '',
                                        ),
                                    ).length
                                }
                                /{itens.length}
                            </span>
                        </h2>
                    )}
                    <Grade>
                        {itens.map(resource => (
                            <CardResource
                                key={resource.id}
                                resource={resource}
                                selecionadas={selecionadas}
                                desabilitado={!can('perfil.editar')}
                                onAlternar={alternar}
                                onMarcarTodos={marcarTodos}
                            />
                        ))}
                    </Grade>
                </Secao>
            ))}
        </Container>
    );
};
export default PermissoesPerfil;
